<template>
  <v-card>
    <v-card-title>
      Users
      <v-spacer />
      <v-chip class="ml-2" small>
        {{ users.length }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <v-row class="mb-4">
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="searchQuery"
            label="Search by name or email"
            prepend-inner-icon="search"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Filter by status"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
      </v-row>

      <v-alert v-if="filteredUsers.length === 0" type="info" text>
        No users found matching your criteria.
      </v-alert>

      <v-data-table
        v-else
        :headers="headers"
        :items="filteredUsers"
        :items-per-page="10"
        class="elevation-1"
      >
        <template #[`item.name`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" class="mr-2">
              <v-icon>person</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="text-caption grey--text">
                {{ item.email }}
              </div>
            </div>
          </div>
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip
            :color="item.status === 'ACTIVE' ? 'success' : 'grey'"
            small
            dark
          >
            {{ item.status }}
          </v-chip>
        </template>

        <template #[`item.type`]="{ item }">
          <v-chip
            :color="isDeakinUser(item) ? 'primary' : 'secondary'"
            small
            outlined
          >
            {{ isDeakinUser(item) ? 'SSO' : 'Password' }}
          </v-chip>
        </template>

        <template #[`item.isSuperAdmin`]="{ item }">
          <v-icon v-if="item.isSuperAdmin" color="warning" small>
            star
          </v-icon>
        </template>

        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                :disabled="actionInProgress"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-if="!item.isSuperAdmin"
                @click="$emit('edit', item)"
              >
                <v-list-item-icon>
                  <v-icon>edit</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="!isDeakinUser(item)"
                @click="$emit('resend-password', item)"
              >
                <v-list-item-icon>
                  <v-icon>mail</v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  >Resend Password Email</v-list-item-title
                >
              </v-list-item>

              <v-divider v-if="!item.isSuperAdmin" />

              <v-list-item
                v-if="item.status === 'ACTIVE' && !item.isSuperAdmin"
                @click="$emit('deactivate', item)"
              >
                <v-list-item-icon>
                  <v-icon color="warning">block</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="warning--text">
                  Deactivate
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="item.status === 'DEACTIVATED'"
                @click="$emit('reactivate', item)"
              >
                <v-list-item-icon>
                  <v-icon color="success">check_circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="success--text">
                  Reactivate
                </v-list-item-title>
              </v-list-item>

              <!--
                SSO (Deakin) users are auto-reprovisioned by Auth0 on their next
                login, so a hard delete does not stick — they must be deactivated
                instead. The server rejects deleting them; hide the action here too.
              -->
              <v-list-item
                v-if="!item.isSuperAdmin && !isDeakinUser(item)"
                @click="$emit('delete', item)"
              >
                <v-list-item-icon>
                  <v-icon color="error">delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="error--text">
                  Delete
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { Researcher } from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'UserList',
  props: {
    users: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, Researcher),
    },
    actionInProgress: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'edit',
    'deactivate',
    'reactivate',
    'delete',
    'resend-password',
  ],
  data: () => ({
    searchQuery: '',
    statusFilter: null,
    headers: [
      { text: 'User', value: 'name', sortable: true },
      {
        text: 'Status',
        value: 'status',
        sortable: true,
        width: '100px',
      },
      {
        text: 'Type',
        value: 'type',
        sortable: false,
        width: '100px',
      },
      {
        text: 'Admin',
        value: 'isSuperAdmin',
        sortable: true,
        width: '80px',
      },
      {
        text: 'Created',
        value: 'createdAt',
        sortable: true,
        width: '120px',
      },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
        width: '80px',
      },
    ],
    statusOptions: [
      { text: 'Active', value: 'ACTIVE' },
      { text: 'Deactivated', value: 'DEACTIVATED' },
    ],
  }),
  computed: {
    filteredUsers() {
      let result = this.users;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (user) =>
            user.name.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query)
        );
      }

      if (this.statusFilter) {
        result = result.filter(
          (user) => user.status === this.statusFilter
        );
      }

      return result;
    },
  },
  methods: {
    isDeakinUser(user) {
      return user.isDeakinUser();
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>
